"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DotnetConditionValidator = void 0;
/*---------------------------------------------------------------------------------------------
*  Licensed to the .NET Foundation under one or more agreements.
*  The .NET Foundation licenses this file to you under the MIT license.
*--------------------------------------------------------------------------------------------*/
const EventStreamEvents_1 = require("../EventStream/EventStreamEvents");
const CommandExecutor_1 = require("../Utils/CommandExecutor");
const FileUtilities_1 = require("../Utils/FileUtilities");
const DotnetCoreAcquisitionWorker_1 = require("./DotnetCoreAcquisitionWorker");
const DotnetResolver_1 = require("./DotnetResolver");
const versionUtils = require("./VersionUtilities");
class DotnetConditionValidator {
    constructor(workerContext, utilityContext, executor) {
        var _a, _b;
        this.workerContext = workerContext;
        this.utilityContext = utilityContext;
        this.executor = executor;
        (_a = this.executor) !== null && _a !== void 0 ? _a : (this.executor = new CommandExecutor_1.CommandExecutor(this.workerContext, this.utilityContext));
        (_b = this.resolver) !== null && _b !== void 0 ? _b : (this.resolver = new DotnetResolver_1.DotnetResolver(this.workerContext, this.utilityContext, this.executor));
    }
    dotnetMeetsRequirement(dotnetExecutablePath, requirement) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            const availableInstalls = yield this.resolver.getDotnetInstalls(dotnetExecutablePath, (_a = requirement.acquireContext.mode) !== null && _a !== void 0 ? _a : 'runtime', requirement.acquireContext.architecture);
            // Assumption : All APIs we call return only one architecture in the group of installs we get (currently a true assumption)
            const determinedInstallArchitecture = (_c = (_b = availableInstalls.at(0)) === null || _b === void 0 ? void 0 : _b.architecture) !== null && _c !== void 0 ? _c : DotnetCoreAcquisitionWorker_1.DotnetCoreAcquisitionWorker.defaultArchitecture();
            if (requirement.acquireContext.mode === 'sdk') {
                if (availableInstalls.some((sdk) => {
                    return this.stringArchitectureMeetsRequirement(determinedInstallArchitecture, requirement.acquireContext.architecture) &&
                        this.stringVersionMeetsRequirement(sdk.version, requirement.acquireContext.version, requirement) && this.allowPreview(sdk.version, requirement);
                })) {
                    this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetConditionsValidated(`${dotnetExecutablePath} satisfies the conditions.`));
                    return true;
                }
            }
            else {
                if (availableInstalls.some((runtime) => {
                    return runtime.mode === requirement.acquireContext.mode && this.stringArchitectureMeetsRequirement(determinedInstallArchitecture, requirement.acquireContext.architecture) &&
                        this.stringVersionMeetsRequirement(runtime.version, requirement.acquireContext.version, requirement) && this.allowPreview(runtime.version, requirement);
                })) {
                    this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetConditionsValidated(`${dotnetExecutablePath} satisfies the conditions.`));
                    return true;
                }
            }
            this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathDidNotMeetCondition(`${dotnetExecutablePath} did NOT satisfy the conditions: hostArch: ${determinedInstallArchitecture}, requiredArch: ${requirement.acquireContext.architecture},
            required version: ${requirement.acquireContext.version}, required mode: ${requirement.acquireContext.mode}`));
            return false;
        });
    }
    stringVersionMeetsRequirement(availableVersion, requestedVersion, requirement) {
        var _a, _b;
        const availableMajor = Number(versionUtils.getMajor(availableVersion, this.workerContext.eventStream, this.workerContext));
        const requestedMajor = Number(versionUtils.getMajor(requestedVersion, this.workerContext.eventStream, this.workerContext));
        const requestedPatchStr = requirement.acquireContext.mode !== 'sdk' ? versionUtils.getRuntimePatchVersionString(requestedVersion, this.workerContext.eventStream, this.workerContext)
            : versionUtils.getSDKCompleteBandAndPatchVersionString(requestedVersion, this.workerContext.eventStream, this.workerContext);
        const requestedPatch = requestedPatchStr ? Number(requestedPatchStr) : null;
        const adjustedVersionSpec = [requirement.versionSpecRequirement].map(x => {
            switch (x) {
                case 'latestMajor':
                    return 'greater_than_or_equal';
                case 'disable':
                    return 'equal';
                default:
                    return x;
            }
        }).at(0);
        if (availableMajor === requestedMajor) {
            const availableMinor = Number(versionUtils.getMinor(availableVersion, this.workerContext.eventStream, this.workerContext));
            const requestedMinor = Number(versionUtils.getMinor(requestedVersion, this.workerContext.eventStream, this.workerContext));
            if (availableMinor === requestedMinor && requestedPatch) {
                const availablePatch = this.getPatchOrFeatureBandWithPatch(availableVersion, requirement);
                switch (adjustedVersionSpec) {
                    // the 'availablePatch' must exist, since the version is from --list-runtimes or --list-sdks, or our internal tracking of installs.
                    case 'equal':
                        return availablePatch === requestedPatch;
                    case 'greater_than_or_equal':
                    case 'latestFeature':
                        return availablePatch >= requestedPatch;
                    case 'less_than_or_equal':
                        return availablePatch <= requestedPatch;
                    case 'latestPatch':
                        const availableBand = this.getFeatureBand(availableVersion, requirement);
                        const requestedBandStr = requirement.acquireContext.mode === 'sdk' ? (_a = versionUtils.getFeatureBandFromVersion(requestedVersion, this.workerContext.eventStream, this.workerContext, false)) !== null && _a !== void 0 ? _a : null : null;
                        const requestedBand = requestedBandStr ? Number(requestedBandStr) : null;
                        return availablePatch >= requestedPatch && (availableBand ? availableBand === requestedBand : true);
                }
            }
            else {
                switch (adjustedVersionSpec) {
                    case 'equal':
                        return availableMinor === requestedMinor;
                    case 'greater_than_or_equal':
                        return availableMinor >= requestedMinor;
                    case 'less_than_or_equal':
                        return availableMinor <= requestedMinor;
                    case 'latestPatch':
                    case 'latestFeature':
                        const availableBand = this.getFeatureBand(availableVersion, requirement);
                        const requestedBandStr = requirement.acquireContext.mode === 'sdk' ? (_b = versionUtils.getFeatureBandFromVersion(requestedVersion, this.workerContext.eventStream, this.workerContext, false)) !== null && _b !== void 0 ? _b : null : null;
                        const requestedBand = requestedBandStr ? Number(requestedBandStr) : null;
                        return availableMinor === requestedMinor && (availableBand ? availableBand === requestedBand : true);
                }
            }
        }
        else {
            switch (adjustedVersionSpec) {
                case 'equal':
                    return false;
                case 'greater_than_or_equal':
                    return availableMajor >= requestedMajor;
                case 'less_than_or_equal':
                    return availableMajor <= requestedMajor;
                case 'latestPatch':
                case 'latestFeature':
                    return false;
            }
        }
    }
    getFeatureBand(availableVersion, requirement) {
        const availableBandStr = requirement.acquireContext.mode === 'sdk' ?
            (() => {
                const featureBand = versionUtils.getFeatureBandFromVersion(availableVersion, this.workerContext.eventStream, this.workerContext, false);
                if (featureBand) {
                    return featureBand;
                }
                return null;
            })() : null;
        return availableBandStr ? Number(availableBandStr) : null;
    }
    getPatchOrFeatureBandWithPatch(availableVersion, requirement) {
        const availablePatchStr = requirement.acquireContext.mode !== 'sdk' ?
            versionUtils.getRuntimePatchVersionString(availableVersion, this.workerContext.eventStream, this.workerContext)
            :
                (() => {
                    const band = versionUtils.getSDKCompleteBandAndPatchVersionString(availableVersion, this.workerContext.eventStream, this.workerContext);
                    if (band) {
                        return band;
                    }
                    return null;
                })();
        const availablePatch = availablePatchStr ? Number(availablePatchStr) : null;
        return availablePatch;
    }
    filterValidPaths(recordPaths, requirement) {
        return recordPaths.filter(installInfo => this.stringVersionMeetsRequirement(installInfo.installRecord.dotnetInstall.version, requirement.acquireContext.version, requirement));
    }
    stringArchitectureMeetsRequirement(outputArchitecture, requiredArchitecture) {
        return !requiredArchitecture || !outputArchitecture || FileUtilities_1.FileUtilities.dotnetInfoArchToNodeArch(outputArchitecture, this.workerContext.eventStream) === requiredArchitecture;
    }
    allowPreview(availableVersion, requirement) {
        if (requirement.rejectPreviews === true) {
            return !versionUtils.isPreviewVersion(availableVersion, this.workerContext.eventStream, this.workerContext);
        }
        return true;
    }
}
exports.DotnetConditionValidator = DotnetConditionValidator;
//# sourceMappingURL=DotnetConditionValidator.js.map